/**
 * Product List Page
 *
 * Implements: US-M5-002 (Manage Products CRUD)
 * AC-156: Product list shows: Name, SKU, Category, Price, Status
 * AC-157: Search and filter products by name, category, status
 * AC-162: Products can be marked as Hot Selling
 * AC-163: Products can be activated/deactivated
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Card,
  IndexTable,
  Text,
  Badge,
  Button,
  Select,
  IndexFilters,
  useSetIndexFiltersMode,
  type IndexFiltersProps,
  Thumbnail,
  InlineStack,
  Box,
  Spinner,
  EmptyState,
  Modal,
  BlockStack,
  Pagination,
} from '@shopify/polaris';
import { PlusIcon, StarFilledIcon, StarIcon, ViewIcon, ExternalIcon } from '@shopify/polaris-icons';
import { productService, type ProductListItem, type ListProductsParams, type Product } from '../../services/product.service';
import { categoryService, type Category } from '../../services/category.service';

const WEB_URL = (import.meta.env.VITE_WEB_URL as string | undefined) || 'http://localhost:5173';

export function ProductListPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<ProductListItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  // Filters
  const [search, setSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedHotSelling, setSelectedHotSelling] = useState<string>('');
  // Stock filter defaults to IN_STOCK so out-of-stock items are hidden by default.
  // It's plain component state, so a page refresh resets it back to IN_STOCK —
  // the admin's "show all" choice is intentionally NOT remembered across reloads.
  const [selectedStock, setSelectedStock] = useState<string>('IN_STOCK');

  // Delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<ProductListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Preview modal (public-view mockup)
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewProduct, setPreviewProduct] = useState<Product | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const handlePreview = useCallback(async (item: ProductListItem) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewProduct(null);
    try {
      setPreviewProduct(await productService.getById(item.id));
    } catch (error) {
      console.error('Failed to load product preview:', error);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const { mode, setMode } = useSetIndexFiltersMode();

  // Load categories
  useEffect(() => {
    categoryService.list().then(setCategories).catch(console.error);
  }, []);

  // Load products
  const loadProducts = useCallback(async (params: ListProductsParams = {}) => {
    setLoading(true);
    try {
      const result = await productService.list({
        page: pagination.page,
        limit: pagination.limit,
        search: search || undefined,
        status: selectedStatus || undefined,
        stockStatus: selectedStock || undefined,
        categoryId: selectedCategory || undefined,
        isHotSelling: selectedHotSelling ? selectedHotSelling === 'true' : undefined,
        // Show in-stock products first so out-of-stock ones sink to the bottom.
        sortBy: 'availability',
        ...params,
      });
      setProducts(result.data || []);
      // Ensure pagination is never undefined
      if (result.pagination) {
        setPagination(result.pagination);
      }
    } catch (error) {
      console.error('Failed to load products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, selectedStatus, selectedStock, selectedCategory, selectedHotSelling]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Handlers
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleStatusChange = useCallback((value: string) => {
    setSelectedStatus(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleCategoryChange = useCallback((value: string) => {
    setSelectedCategory(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleHotSellingChange = useCallback((value: string) => {
    setSelectedHotSelling(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleStockChange = useCallback((value: string) => {
    setSelectedStock(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearch('');
    setSelectedStatus('');
    setSelectedCategory('');
    setSelectedHotSelling('');
    // Clearing filters returns to the DEFAULT view (in-stock only), not "show all".
    setSelectedStock('IN_STOCK');
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleToggleHotSelling = useCallback(async (product: ProductListItem) => {
    try {
      await productService.toggleHotSelling(product.id);
      loadProducts();
    } catch (error) {
      console.error('Failed to toggle hot selling:', error);
    }
  }, [loadProducts]);

  const handleToggleStatus = useCallback(async (product: ProductListItem) => {
    try {
      await productService.toggleStatus(product.id);
      loadProducts();
    } catch (error) {
      console.error('Failed to toggle status:', error);
    }
  }, [loadProducts]);

  const handleDeleteClick = useCallback((product: ProductListItem) => {
    setProductToDelete(product);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!productToDelete) return;
    setDeleting(true);
    try {
      await productService.delete(productToDelete.id);
      setDeleteModalOpen(false);
      setProductToDelete(null);
      loadProducts();
    } catch (error) {
      console.error('Failed to delete product:', error);
    } finally {
      setDeleting(false);
    }
  }, [productToDelete, loadProducts]);

  // Filter options
  const statusOptions = [
    { label: 'All Statuses', value: '' },
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Inactive', value: 'INACTIVE' },
  ];

  const categoryOptions = [
    { label: 'All Categories', value: '' },
    ...categories.map((cat) => ({ label: cat.name, value: cat.id })),
  ];

  const hotSellingOptions = [
    { label: 'All Products', value: '' },
    { label: 'Hot Selling', value: 'true' },
    { label: 'Regular', value: 'false' },
  ];

  const stockOptions = [
    { label: 'In Stock', value: 'IN_STOCK' },
    { label: 'Out of Stock', value: 'OUT_OF_STOCK' },
    { label: 'Show all', value: '' },
  ];

  const filters: IndexFiltersProps['filters'] = [
    {
      key: 'stock',
      label: 'Availability',
      filter: (
        <Select
          label="Availability"
          labelHidden
          options={stockOptions}
          value={selectedStock}
          onChange={handleStockChange}
        />
      ),
      shortcut: true,
    },
    {
      key: 'status',
      label: 'Status',
      filter: (
        <Select
          label="Status"
          labelHidden
          options={statusOptions}
          value={selectedStatus}
          onChange={handleStatusChange}
        />
      ),
      shortcut: true,
    },
    {
      key: 'category',
      label: 'Category',
      filter: (
        <Select
          label="Category"
          labelHidden
          options={categoryOptions}
          value={selectedCategory}
          onChange={handleCategoryChange}
        />
      ),
      shortcut: true,
    },
    {
      key: 'hotSelling',
      label: 'Hot Selling',
      filter: (
        <Select
          label="Hot Selling"
          labelHidden
          options={hotSellingOptions}
          value={selectedHotSelling}
          onChange={handleHotSellingChange}
        />
      ),
    },
  ];

  const appliedFilters: IndexFiltersProps['appliedFilters'] = [];
  // Always show the availability chip when a stock filter is active (it is by
  // default = In Stock). Removing it switches to "Show all" so out-of-stock appear.
  if (selectedStock) {
    appliedFilters.push({
      key: 'stock',
      label: `Availability: ${selectedStock === 'IN_STOCK' ? 'In Stock' : 'Out of Stock'}`,
      onRemove: () => setSelectedStock(''),
    });
  }
  if (selectedStatus) {
    appliedFilters.push({
      key: 'status',
      label: `Status: ${selectedStatus}`,
      onRemove: () => setSelectedStatus(''),
    });
  }
  if (selectedCategory) {
    const cat = categories.find((c) => c.id === selectedCategory);
    appliedFilters.push({
      key: 'category',
      label: `Category: ${cat?.name || selectedCategory}`,
      onRemove: () => setSelectedCategory(''),
    });
  }
  if (selectedHotSelling) {
    appliedFilters.push({
      key: 'hotSelling',
      label: `Hot Selling: ${selectedHotSelling === 'true' ? 'Yes' : 'No'}`,
      onRemove: () => setSelectedHotSelling(''),
    });
  }

  const rowMarkup = products.map((product, index) => (
    <IndexTable.Row
      id={product.id}
      key={product.id}
      position={index}
    >
      <IndexTable.Cell>
        <InlineStack gap="300" blockAlign="center">
          <Thumbnail
            source={product.primaryImage || 'https://placehold.co/40x40/e2e8f0/64748b?text=No+Img'}
            alt={product.name}
            size="small"
          />
          <Box maxWidth="320px">
            <BlockStack gap="100">
              <Text variant="bodyMd" fontWeight="semibold" as="span" truncate>
                {product.name}
              </Text>
              <Text variant="bodySm" tone="subdued" as="span">
                {product.sku}
              </Text>
            </BlockStack>
          </Box>
        </InlineStack>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodyMd">
          {product.categoryName}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodyMd">
          {(() => {
            const price = product.startingPrice ?? product.samplePrice;
            return price != null ? `₹${price.toLocaleString()}` : '-';
          })()}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={product.status === 'ACTIVE' ? 'success' : 'critical'}>
          {product.status}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={product.stockStatus === 'IN_STOCK' ? 'success' : 'warning'}>
          {product.stockStatus === 'IN_STOCK' ? 'In Stock' : 'Out of Stock'}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Button
          icon={product.isHotSelling ? StarFilledIcon : StarIcon}
          variant="plain"
          onClick={() => handleToggleHotSelling(product)}
          accessibilityLabel={product.isHotSelling ? 'Remove from hot selling' : 'Mark as hot selling'}
        />
      </IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack gap="200">
          <Button
            size="slim"
            icon={ViewIcon}
            onClick={() => handlePreview(product)}
            accessibilityLabel="Preview public view"
          >
            Preview
          </Button>
          <Button size="slim" onClick={() => navigate(`/products/${product.id}`)}>
            Edit
          </Button>
          <Button
            size="slim"
            tone={product.status === 'ACTIVE' ? 'critical' : 'success'}
            onClick={() => handleToggleStatus(product)}
          >
            {product.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
          </Button>
          <Button size="slim" tone="critical" onClick={() => handleDeleteClick(product)}>
            Delete
          </Button>
        </InlineStack>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  const emptyState = (
    <EmptyState
      heading="No products found"
      image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
      action={{
        content: 'Add product',
        onAction: () => navigate('/products/new'),
      }}
    >
      <p>Add products to get started.</p>
    </EmptyState>
  );

  return (
    <Page
      title="Products"
      primaryAction={{
        content: 'Add product',
        icon: PlusIcon,
        onAction: () => navigate('/products/new'),
      }}
    >
      <Card padding="0">
        <IndexFilters
          queryValue={search}
          queryPlaceholder="Search products..."
          onQueryChange={handleSearchChange}
          onQueryClear={() => setSearch('')}
          filters={filters}
          appliedFilters={appliedFilters}
          onClearAll={handleClearFilters}
          mode={mode}
          setMode={setMode}
          tabs={[]}
          selected={0}
        />
        {loading ? (
          <Box padding="600">
            <InlineStack align="center">
              <Spinner size="large" />
            </InlineStack>
          </Box>
        ) : products.length === 0 ? (
          emptyState
        ) : (
          <>
            <IndexTable
              itemCount={products.length}
              headings={[
                { title: 'Product' },
                { title: 'Category' },
                { title: 'Price' },
                { title: 'Status' },
                { title: 'Stock' },
                { title: 'Hot Selling' },
                { title: 'Actions' },
              ]}
              selectable={false}
            >
              {rowMarkup}
            </IndexTable>
            <Box padding="400">
              <InlineStack align="center">
                <Pagination
                  hasPrevious={pagination.page > 1}
                  hasNext={pagination.page < pagination.totalPages}
                  onPrevious={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  onNext={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                />
              </InlineStack>
              <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                Showing {products.length} of {pagination.total} products
              </Text>
            </Box>
          </>
        )}
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete product?"
        primaryAction={{
          content: 'Delete',
          destructive: true,
          loading: deleting,
          onAction: handleDeleteConfirm,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setDeleteModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <Text as="p">
            Are you sure you want to delete "{productToDelete?.name}"? This action cannot be undone.
          </Text>
        </Modal.Section>
      </Modal>

      {/* Public-view Preview Modal */}
      <Modal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title="Public view preview"
        size="large"
        secondaryActions={[{ content: 'Close', onAction: () => setPreviewOpen(false) }]}
      >
        <Modal.Section>
          {previewLoading || !previewProduct ? (
            <Box padding="600">
              <InlineStack align="center">
                <Spinner size="large" />
              </InlineStack>
            </Box>
          ) : (
            (() => {
              const img =
                previewProduct.images?.find((i) => i.isPrimary)?.imageUrl ||
                previewProduct.images?.[0]?.imageUrl ||
                null;
              return (
                <BlockStack gap="400">
                  <div style={{ textAlign: 'center' }}>
                    {img ? (
                      <img
                        src={img}
                        alt={previewProduct.name}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '440px',
                          objectFit: 'contain',
                          borderRadius: 8,
                          border: '1px solid #e1e3e5',
                        }}
                      />
                    ) : (
                      <Box padding="800" background="bg-surface-secondary" borderRadius="200">
                        <Text as="span" tone="subdued">No image yet</Text>
                      </Box>
                    )}
                  </div>

                  <BlockStack gap="200">
                    <Text as="h2" variant="headingLg">{previewProduct.name}</Text>
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="span" tone="subdued" variant="bodySm">SKU {previewProduct.sku}</Text>
                      <Badge tone={previewProduct.status === 'ACTIVE' ? 'success' : 'critical'}>
                        {previewProduct.status}
                      </Badge>
                    </InlineStack>
                    {previewProduct.samplePrice != null && (
                      <Text as="p" variant="headingMd">
                        MRP ₹{Number(previewProduct.samplePrice).toLocaleString()}
                      </Text>
                    )}
                  </BlockStack>

                  {previewProduct.description && (
                    <BlockStack gap="100">
                      <Text as="h3" variant="headingSm">Description</Text>
                      <div style={{ whiteSpace: 'pre-line', color: '#42474c', fontSize: 14, lineHeight: 1.5 }}>
                        {previewProduct.description}
                      </div>
                    </BlockStack>
                  )}

                  <Box paddingBlockStart="200">
                    <Button
                      icon={ExternalIcon}
                      url={`${WEB_URL}/products/${previewProduct.slug}`}
                      target="_blank"
                      disabled={previewProduct.status !== 'ACTIVE'}
                    >
                      {previewProduct.status === 'ACTIVE' ? 'Open live page' : 'Open live page (activate first)'}
                    </Button>
                  </Box>
                </BlockStack>
              );
            })()
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}
